import type { TierId } from '~/config/subscription-tiers.config';

/**
 * Page-section visibility toggles (rows of `page_section_visibility`).
 *
 * `main_*` applies to anonymous visitors (landing, drug pages, /pricing),
 * `nav_*` to logged-in users (header links, paywall, /pricing).
 * `*_pricing_*` sub-toggles pick which audience tabs and tiers the pricing
 * section shows; they only matter while the scope's `*_pricing` is on.
 * `main_incredibles_bar` shows the "Finalista X edycji InCredibles" bar above
 * the header; `main_incredibles_bar_black` picks the black variant (off = citron).
 */
export const SECTION_IDS = [
  'main_pricing',
  'main_pricing_individual',
  'main_pricing_tier_small',
  'main_pricing_tier_medium',
  'main_pricing_tier_large',
  'main_pricing_organization',
  'main_about',
  'main_incredibles_bar',
  'main_incredibles_bar_black',
  'nav_pricing',
  'nav_pricing_individual',
  'nav_pricing_tier_small',
  'nav_pricing_tier_medium',
  'nav_pricing_tier_large',
  'nav_pricing_organization',
  'nav_pricing_custom_plan',
  'nav_about',
] as const;

export type SectionVisibilityId = (typeof SECTION_IDS)[number];

export type SectionVisibilityMap = Record<SectionVisibilityId, boolean>;

export type PricingScope = 'main' | 'nav';

export interface PricingVisibility {
  individual: boolean;
  organization: boolean;
  tiers: TierId[];
  /** Show an assigned user's dedicated plan card (logged-in scope only). */
  customPlan: boolean;
}

const TIER_IDS: TierId[] = ['small', 'medium', 'large'];

export function createHiddenVisibility(): SectionVisibilityMap {
  return Object.fromEntries(
    SECTION_IDS.map((id) => [id, false]),
  ) as SectionVisibilityMap;
}

/** Which audience tabs and tiers the pricing section shows for a scope. */
export function getPricingVisibility(
  visibility: SectionVisibilityMap,
  scope: PricingScope,
): PricingVisibility {
  const tiers = TIER_IDS.filter(
    (tier) => visibility[`${scope}_pricing_tier_${tier}`],
  );

  return {
    // The individual tab is empty without at least one tier
    individual: visibility[`${scope}_pricing_individual`] && tiers.length > 0,
    organization: visibility[`${scope}_pricing_organization`],
    tiers,
    customPlan: scope === 'nav' && visibility.nav_pricing_custom_plan,
  };
}

/**
 * Turn a scope's `*_pricing` off when its sub-toggles leave nothing to show,
 * so links, paywall CTAs and /pricing never lead to an empty pricing section.
 */
export function normalizeVisibility(
  visibility: SectionVisibilityMap,
): SectionVisibilityMap {
  const normalized = { ...visibility };

  for (const scope of ['main', 'nav'] as const) {
    const pricing = getPricingVisibility(visibility, scope);

    if (!pricing.individual && !pricing.organization) {
      normalized[`${scope}_pricing`] = false;
    }
  }

  return normalized;
}
